import React, { useState, useMemo } from "react";
import {
  Box,
  Paper,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Typography,
} from "@mui/material";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area,
  ComposedChart,
  Legend,
  ScatterChart,
  Scatter,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  CartesianGrid,
} from "recharts";

const COLORS = [
  "#2563eb",
  "#9333ea",
  "#db2777",
  "#fde047",
  "#34d399",
  "#f97316",
  "#8b5cf6",
];

const CustomTooltip = ({ active, payload, label, darkMode }) => {
  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          bgcolor: darkMode ? "#334155" : "rgba(255,255,255,0.95)",
          p: 1.5,
          borderRadius: 2,
          boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
          color: darkMode ? "#e2e8f0" : "#1e293b",
          minWidth: 120,
        }}
      >
        <Typography variant="subtitle2">
          <strong>{label}</strong>
        </Typography>
        {payload.map((p, i) => (
          <Typography
            key={i}
            variant="body2"
          >{`${p.name}: ${p.value}`}</Typography>
        ))}
      </Box>
    );
  }
  return null;
};

export default function Visualization({ rows, schema, darkMode }) {
  const [chartType, setChartType] = useState("bar");
  const [xColumn, setXColumn] = useState("");
  const [yColumn, setYColumn] = useState("");
  const [aggMethod, setAggMethod] = useState("sum");

  // Merge schema columns and row keys
  const rowKeys = Object.keys(rows?.[0] || {});
  const schemaCols = schema?.columns?.map((c) => c.name) || [];
  const allColumns = Array.from(new Set([...schemaCols, ...rowKeys]));

  const xKey = xColumn || allColumns[0] || "";
  const yKey = yColumn || allColumns[1] || allColumns[0] || "";

  const data = useMemo(() => {
    if (!rows || rows.length === 0) return [];

    const temp = {};
    rows.forEach((r) => {
      const key = r[xKey] ?? "—";
      const val = !isNaN(Number(r[yKey])) ? Number(r[yKey]) : r[yKey];
      if (!temp[key]) temp[key] = [];
      temp[key].push(val);
    });

    return Object.entries(temp).map(([name, vals]) => {
      let value;
      const allNumeric = vals.every((v) => typeof v === "number");
      if (allNumeric) {
        switch (aggMethod) {
          case "sum":
            value = vals.reduce((a, b) => a + b, 0);
            break;
          case "avg":
            value = vals.reduce((a, b) => a + b, 0) / vals.length;
            break;
          case "min":
            value = Math.min(...vals);
            break;
          case "max":
            value = Math.max(...vals);
            break;
          case "count":
          default:
            value = vals.length;
        }
      } else {
        value = vals.length;
      }
      return { name, value };
    });
  }, [rows, xKey, yKey, aggMethod]);

  if (!rows || rows.length === 0)
    return (
      <Typography
        variant="body2"
        sx={{ textAlign: "center", color: darkMode ? "#94a3b8" : "#64748b" }}
      >
        No data to visualize.
      </Typography>
    );

  if (data.length === 0)
    return (
      <Typography
        variant="body2"
        sx={{ textAlign: "center", color: darkMode ? "#94a3b8" : "#64748b" }}
      >
        No aggregated data to show.
      </Typography>
    );

  const axisStroke = darkMode ? "#e2e8f0" : "#1e293b";
  const gridStroke = darkMode ? "#475569" : "#e2e8f0";

  return (
    <Box>
      {/* Controls Panel */}
      <Paper
        elevation={4}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 3,
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          bgcolor: darkMode ? "#1e293b" : "#fff",
        }}
      >
        <FormControl sx={{ flex: "1 1 200px" }} fullWidth>
          <InputLabel shrink>X-Axis</InputLabel>
          <Select value={xColumn} onChange={(e) => setXColumn(e.target.value)}>
            {allColumns.map((col) => (
              <MenuItem key={col} value={col}>
                {col}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ flex: "1 1 200px" }} fullWidth>
          <InputLabel shrink>Y-Axis</InputLabel>
          <Select value={yColumn} onChange={(e) => setYColumn(e.target.value)}>
            {allColumns.map((col) => (
              <MenuItem key={col} value={col}>
                {col}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ flex: "1 1 200px" }} fullWidth>
          <InputLabel shrink>Aggregation</InputLabel>
          <Select
            value={aggMethod}
            onChange={(e) => setAggMethod(e.target.value)}
          >
            <MenuItem value="sum">Sum</MenuItem>
            <MenuItem value="avg">Average</MenuItem>
            <MenuItem value="count">Count</MenuItem>
            <MenuItem value="min">Min</MenuItem>
            <MenuItem value="max">Max</MenuItem>
          </Select>
        </FormControl>

                <FormControl sx={{ flex: "1 1 200px" }} fullWidth>
          <InputLabel shrink>Chart Type</InputLabel>
         <Select
            value={chartType}
            onChange={(e) => setChartType(e.target.value)}
          >
            <MenuItem value="bar">Bar</MenuItem>
            <MenuItem value="line">Line</MenuItem>
            <MenuItem value="pie">Pie</MenuItem>
            <MenuItem value="scatter">Scatter</MenuItem>
            <MenuItem value="area">Area</MenuItem>
            <MenuItem value="composed">Composed</MenuItem>
            <MenuItem value="radar">Radar</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      {/* Chart */}
      <Box sx={{ width: "100%", height: 400 }}>
        {chartType === "bar" && (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}>
              <CartesianGrid stroke={gridStroke} strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                stroke={axisStroke}
                tick={{ fill: axisStroke }}
              />
              <YAxis stroke={axisStroke} tick={{ fill: axisStroke }} />
              <Tooltip content={<CustomTooltip darkMode={darkMode} />} />
              <Legend wrapperStyle={{ color: axisStroke }} />
              <Bar dataKey="value">
                {data.map((d, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}

        {chartType === "line" && (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <CartesianGrid stroke={gridStroke} strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                stroke={axisStroke}
                tick={{ fill: axisStroke }}
              />
              <YAxis stroke={axisStroke} tick={{ fill: axisStroke }} />
              <Tooltip content={<CustomTooltip darkMode={darkMode} />} />
              <Legend wrapperStyle={{ color: axisStroke }} />
              <Line
                type="monotone"
                dataKey="value"
                stroke="#2563eb"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        )}

        {chartType === "pie" && (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                outerRadius={120}
                label
              >
                {data.map((d, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip darkMode={darkMode} />} />
            </PieChart>
          </ResponsiveContainer>
        )}

        {chartType === "scatter" && (
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart>
              <CartesianGrid stroke={gridStroke} />
              <XAxis
                type="category"
                dataKey="name"
                stroke={axisStroke}
                tick={{ fill: axisStroke }}
              />
              <YAxis
                type="number"
                dataKey="value"
                stroke={axisStroke}
                tick={{ fill: axisStroke }}
              />
              <Tooltip content={<CustomTooltip darkMode={darkMode} />} />
              <Scatter data={data} fill="#9333ea" />
            </ScatterChart>
          </ResponsiveContainer>
        )}

        {chartType === "area" && (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data}>
              <CartesianGrid stroke={gridStroke} strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                stroke={axisStroke}
                tick={{ fill: axisStroke }}
              />
              <YAxis stroke={axisStroke} tick={{ fill: axisStroke }} />
              <Tooltip content={<CustomTooltip darkMode={darkMode} />} />
              <Area
                type="monotone"
                dataKey="value"
                stroke="#2563eb"
                fill="#93c5fd"
              />
            </AreaChart>
          </ResponsiveContainer>
        )}

        {chartType === "composed" && (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data}>
              <CartesianGrid stroke={gridStroke} strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                stroke={axisStroke}
                tick={{ fill: axisStroke }}
              />
              <YAxis stroke={axisStroke} tick={{ fill: axisStroke }} />
              <Tooltip content={<CustomTooltip darkMode={darkMode} />} />
              <Legend wrapperStyle={{ color: axisStroke }} />
              <Area
                type="monotone"
                dataKey="value"
                fill="#93c5fd"
                stroke="#2563eb"
              />
              <Bar dataKey="value" fill="#2563eb" />
              <Line type="monotone" dataKey="value" stroke="#9333ea" />
            </ComposedChart>
          </ResponsiveContainer>
        )}

        {chartType === "radar" && (
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={data}>
              <PolarGrid stroke={gridStroke} />
              <PolarAngleAxis
                dataKey="name"
                stroke={axisStroke}
                tick={{ fill: axisStroke }}
              />
              <PolarRadiusAxis stroke={axisStroke} />
              <Radar
                dataKey="value"
                stroke="#2563eb"
                fill="#93c5fd"
                fillOpacity={0.6}
              />
              <Tooltip content={<CustomTooltip darkMode={darkMode} />} />
            </RadarChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Box>
  );
}
